import { graphql } from '~/graphql/generated/gql';

export const studentRoleFragment = graphql(`
  fragment StudentRoleFields on StudentRole {
    id
    firstName
    lastName
    orgType
    contextType
    orgId
    school {
      id
      name
      label
      clusterId
      isPersonalSchool
    }
    classYear
    isGraduated
    birthDate
    gender
    __typename
    createdAt
    state {
      id
      name
      code
    }
    isManageReferred
    isDemoRole
    isPhoneNumberProvidedAndVerified
    phoneNumber
    isManaged
    lockedFields
    user {
      id
      primaryEmail
    }
  }
`);

export const dashboardStudentRoleFragment = graphql(`
  fragment DashboardStudentRoleFields on StudentRole {
    id
    firstName
    lastName
    orgType
    contextType
    orgId
    classYear
    isGraduated
    user {
      id
      primaryEmail
      pathwayWalletItemValuesCount
    }
    isDemoRole
  }
`);

export const studentOutcomesRoleFragment = graphql(`
  fragment StudentOutcomesRoleFields on StudentRole {
    id
    firstName
    lastName
    classYear
    user {
      primaryEmail
      userProgressCount
    }
    isDemoRole
  }
`);

export const schoolStudentRoleInviteCountsFragment = graphql(`
  fragment SchoolStudentRoleInviteCountsFields on School {
    studentRoleRequestsCount
    studentRoleInvitesCount
  }
`);

export const teacherRoleFragment = graphql(`
  fragment TeacherRoleFields on TeacherRole {
    id
    firstName
    lastName
    jobTitle
    teacherDisplayName
    orgType
    contextType
    orgId
    isPersonalTeacherRole
    school {
      id
      name
      label
      clusterId
      stateId
      ...SchoolStudentRoleInviteCountsFields
      isPersonalSchool
      verified
      orgRoleSource {
        connectionName
        provider
        paused
      }
    }
    state {
      id
      name
      code
    }
  }
`);

export const adminRoleFragment = graphql(`
  fragment AdminRoleFields on AdminRole {
    id
    contextType
    firstName
    lastName
    capabilities
  }
`);

// @todo decide if we want to get latest coins transaction on initial self call
export const authUserFragment = graphql(`
  fragment AuthUserFields on User {
    id
    externalId
    primaryEmail
    animationsEnabled
    avatarIllustration
    avatarColor
    unifiedOnboardingComplete
    isRosterManaged
    isGhostStudent
    isGhostLearner
    type
    accountProduct
    isReferredUser
    hasPassword
    adminMfaStatus
    switchableAccounts {
      accountId
      schoolName
      roleType
      isPersonal
    }
    currentAccountChoice {
      accountId
      schoolName
      roleType
      isPersonal
    }
    accountState {
      id
      code
    }
    latestRedemption {
      id
      createdAt
    }
    teacherRoles {
      ...TeacherRoleFields
    }
    studentRoles {
      ...StudentRoleFields
    }
    accountStudentRole {
      ...StudentRoleFields
    }
    adminRoles {
      ...AdminRoleFields
    }
    latestCoinBalance
    earnedBadgesCount
    clusterTermsRequirement
    schoolsAwaitingTermsAcceptance {
      id
      name
    }
    signInQuestions {
      aboutYouFields
      showMarketingBox
      latestBirthMonthYear
    }
    considerationScore {
      percentage
      scoreRisk
    }
    accountDetailsChecks {
      isAccountDetailsCompleted
      hasFirstName
      hasLastName
      hasVerifiedPhone
      hasState
      hasBirthDate
      hasGender
    }
  }
`);

export const userLoginFragment = graphql(`
  fragment UserLoginFields on UserLogin {
    id
    type
    value
    isPrimary
    verifiedAt
  }
`);

export const studentRoleRequestFragment = graphql(`
  fragment StudentRoleRequestFields on StudentRoleRequest {
    id
    firstName
    lastName
    email
    classYear
    status
    createdAt
  }
`);

export const schoolRequestFragment = graphql(`
  fragment SchoolRequestFields on StudentRoleRequest {
    id
    schoolId
    school {
      id
      name
      label
      clusterId
    }
    classYear
    status
    __typename
    createdAt
  }
`);

export const studentRoleInviteFragment = graphql(`
  fragment StudentRoleInviteFields on StudentRoleInvite {
    id
    schoolId
    school {
      id
      name
      label
      displayName
      clusterId
    }
    classYear
    status
    __typename
    createdAt
  }
`);

export const schoolStudentRoleInviteFragment = graphql(`
  fragment SchoolStudentRoleInviteFields on StudentRoleInvite {
    id
    firstName
    lastName
    email
    classYear
    status
    createdAt
  }
`);

export const schoolManagementDataFragment = graphql(`
  fragment SchoolManagementUserFields on User {
    id
    studentRoles {
      ...StudentRoleFields
    }
    studentRoleRequests {
      ...SchoolRequestFields
    }
    studentRoleInvites {
      ...StudentRoleInviteFields
    }
  }
`);

export const paginatorInfoFragment = graphql(`
  fragment PaginatorInfoFields on PaginatorInfo {
    hasMorePages
    currentPage
    lastPage
    total
  }
`);

export const coreSiteConfig = graphql(`
  query coreSiteConfig {
    core {
      site {
        publicSiteConfig {
          country {
            id
            name
            code
            defaultLocale
            currency
            rolloverDay
            rolloverMonth
            firstYearGroup
            lastYearGroup
            displayAsGraduatedStartYearGroup
            currentClassYearOptions {
              grade
              year
            }
            allClassYearOptions {
              grade
              year
            }
            timezone
          }
          sessionLifetime
          referralRewards {
            refereeAmount
            referrerAmount
            isCash
            refereeCoinsToQualify
          }
        }
        i18nMessages
        acceptedFileTypes {
          value {
            type
            mimes
            extensions
          }
        }
      }
      org {
        clusters {
          id
          name
          code
          host
          pathPrefix
          product
          schoolDataClusterId
          country {
            id
            code
            name
            states {
              id
              name
              code
              marketingBoxStartsTicked
            }
          }
          planProductName
          manageProductName
          allowOtherSchoolsInRegistration
          hasAgeConsentCheck
          manageRegisterEnabled
          teacherRegistrationOpen
          showDistrictInSchoolsRegistration
          suppressMarketingTechnologies
          allowedAnalyticsProviders
          coinsPerDollar
          cashRedemptionEnabled: redemptionEnabled(type: CASH)
          giftcardRedemptionEnabled: redemptionEnabled(type: GIFTCARD)
          integrationsConfig {
            loginButtons
          }
        }
      }
    }
  }
`);

export const requestLoginCodeMutation = graphql(`
  mutation requestLoginCode($input: RequestLoginCodeInput!) {
    core {
      auth {
        requestLoginCode(input: $input) {
          success
        }
      }
    }
  }
`);

export const verifyLoginCodeMutation = graphql(`
  mutation login($input: VerifyLoginCodeInput!) {
    core {
      auth {
        verifyLoginCode(input: $input) {
          flow
          hasMatchedInvite
          accountCreated
        }
      }
    }
  }
`);

export const loginWithPasswordMutation = graphql(`
  mutation loginWithPassword($input: LoginWithPasswordInput!) {
    core {
      auth {
        loginWithPassword(input: $input) {
          flow
          hasMatchedInvite
          accountCreated
        }
      }
    }
  }
`);

export const RESET_PASSWORD_AFTER_CODE_MUTATION = graphql(`
  mutation resetPasswordAfterCode($input: ResetPasswordAfterCodeInput!) {
    core {
      auth {
        resetPasswordAfterCode(input: $input) {
          flow
          hasMatchedInvite
          passwordChangeToken
        }
      }
    }
  }
`);

export const PENDING_ACCOUNT_CHOICES_QUERY = graphql(`
  query pendingAccountChoices {
    core {
      auth {
        pendingAccountChoices {
          accountId
          schoolName
          roleType
          isPersonal
        }
      }
    }
  }
`);

export const SIGN_IN_AWAITS_PASSWORD_QUERY = graphql(`
  query signInAwaitsPassword($email: String!) {
    core {
      auth {
        signInAwaitsPassword(email: $email)
      }
    }
  }
`);

export const CHOOSE_ACCOUNT_MUTATION = graphql(`
  mutation chooseAccount($input: ChooseAccountInput!) {
    core {
      auth {
        chooseAccount(input: $input) {
          flow
          hasMatchedInvite
          passwordChangeToken
          providerLoginFailure {
            screen
            attemptId
            launchedFromProvider
          }
        }
      }
    }
  }
`);

export const SWITCH_ACCOUNT_MUTATION = graphql(`
  mutation switchAccount($input: SwitchAccountInput!) {
    core {
      auth {
        switchAccount(input: $input) {
          account {
            id
          }
        }
      }
    }
  }
`);

export const beginAdminMfaEnrolmentMutation = graphql(`
  mutation beginAdminMfaEnrolment {
    core {
      auth {
        beginAdminMfaEnrolment {
          secret
          qrCodeSvg
        }
      }
    }
  }
`);

export const confirmAdminMfaEnrolmentMutation = graphql(`
  mutation confirmAdminMfaEnrolment($input: ConfirmAdminMfaEnrolmentInput!) {
    core {
      auth {
        confirmAdminMfaEnrolment(input: $input) {
          flow
          recoveryCodes
        }
      }
    }
  }
`);

export const verifyAdminMfaMutation = graphql(`
  mutation verifyAdminMfa($input: VerifyAdminMfaInput!) {
    core {
      auth {
        verifyAdminMfa(input: $input) {
          flow
        }
      }
    }
  }
`);

export const initiateSocialLoginMutation = graphql(`
  mutation initiateSocialLogin($input: InitiateSocialLoginInput!) {
    core {
      auth {
        initiateSocialLogin(input: $input) {
          redirectUrl
        }
      }
    }
  }
`);

export const exchangeSocialLoginMutation = graphql(`
  mutation exchangeSocialLogin($input: ExchangeSocialLoginInput!) {
    core {
      auth {
        exchangeSocialLogin(input: $input) {
          flow
          email
          firstName
          lastName
          returnUrl
          provider
          entrySource
          entryCta
          authStartTime
          utmKey
        }
      }
    }
  }
`);

export const providerLoginPayloadFieldsFragment = graphql(`
  fragment ProviderLoginPayloadFields on ProviderLoginPayload {
    __typename
    ... on ProviderLoginRedirect {
      providerUrl
    }
    ... on ProviderLoginSignedIn {
      landingPath
    }
    ... on ProviderLoginAccountChoiceRequired {
      email
      landingPath
    }
    ... on ProviderLoginFailure {
      screen
      attemptId
      launchedFromProvider
    }
    ... on ProviderLoginProofRequired {
      attemptId
      maskedAddress
      launchedFromProvider
      codeRejected
    }
  }
`);

export const startProviderLoginMutation = graphql(`
  mutation startProviderLogin($input: StartProviderLoginInput!) {
    core {
      auth {
        startProviderLogin(input: $input) {
          ...ProviderLoginPayloadFields
        }
      }
    }
  }
`);

export const completeProviderLoginMutation = graphql(`
  mutation completeProviderLogin($input: CompleteProviderLoginInput!) {
    core {
      auth {
        completeProviderLogin(input: $input) {
          ...ProviderLoginPayloadFields
        }
      }
    }
  }
`);

export const completeProviderLoginProofMutation = graphql(`
  mutation completeProviderLoginProof($input: CompleteProviderLoginProofInput!) {
    core {
      auth {
        completeProviderLoginProof(input: $input) {
          ...ProviderLoginPayloadFields
        }
      }
    }
  }
`);

export const logoutMutation = graphql(`
  mutation logout {
    core {
      auth {
        logout {
          success
        }
      }
    }
  }
`);

export const ACCEPT_CLUSTER_TERMS = graphql(`
  mutation acceptClusterTerms {
    acceptClusterTerms {
      id
      enrolledAt
      acceptedAt
    }
  }
`);

export const ACCEPT_SCHOOL_TERMS = graphql(`
  mutation acceptSchoolTerms($schoolId: ID!) {
    acceptSchoolTerms(schoolId: $schoolId) {
      id
      acceptedAt
    }
  }
`);

export const JOIN_SCHOOL_FROM_INVITE = graphql(`
  mutation joinSchoolFromInvite($studentRoleInviteId: ID!) {
    joinSchoolFromInvite(studentRoleInviteId: $studentRoleInviteId) {
      success
    }
  }
`);

export const SITE_CLUSTER_TERMS_CURRENT_VERSION = graphql(`
  query siteClusterTermsCurrentVersion($audience: ClusterTermsAudience!) {
    core {
      site {
        clusterTermsCurrentVersion(audience: $audience) {
          id
          audience
          version
          content
        }
      }
    }
  }
`);

export const signUpStudentMutation = graphql(`
  mutation signUpStudent($input: SignUpStudentInput!) {
    core {
      user {
        signUpStudent(input: $input) {
          success
          passwordChangeToken
        }
      }
    }
  }
`);
export const createGhostStudentMutation = graphql(`
  mutation createGhostStudent($input: CreateGhostStudentInput!) {
    core {
      user {
        createGhostStudent(input: $input) {
          success
        }
      }
    }
  }
`);

export const signUpTeacherMutation = graphql(`
  mutation signUpTeacher($input: SignUpTeacherInput!) {
    core {
      user {
        signUpTeacher(input: $input) {
          success
          passwordChangeToken
        }
      }
    }
  }
`);

export const selfQuery = graphql(`
  query self {
    core {
      user {
        self {
          ...AuthUserFields
        }
      }
    }
  }
`);

export const directorySchoolsQuery = graphql(`
  query directorySchools($schoolFilters: SchoolFilters) {
    core {
      org {
        school {
          directorySchools(filters: $schoolFilters, page: 1, first: 50) {
            data {
              id
              name
              label
            }
          }
        }
      }
    }
  }
`);

export const getSchoolStudentRoles = graphql(`
  query getSchoolStudentRoles($page: Int!, $schoolId: ID!, $filters: StudentRoleFilters, $orderBy: [SchoolStudentRolesOrderByOrderByClause!]) {
    core {
      org {
        school {
          school(id: $schoolId) {
            id
            studentRoles(page: $page, filters: $filters, orderBy: $orderBy) {
              data {
                ...DashboardStudentRoleFields
              }
              paginatorInfo {
                ...PaginatorInfoFields
              }
            }
          }
        }
      }
    }
  }
`);

// Student Outcomes page - new query with first/perPage for rows per page control
export const getStudentOutcomes = graphql(`
  query getStudentOutcomes(
    $page: Int!
    $first: Int
    $schoolId: ID!
    $filters: StudentRoleFilters
    $orderBy: [SchoolStudentRolesOrderByOrderByClause!]
  ) {
    core {
      org {
        school {
          school(id: $schoolId) {
            id
            studentRoles(page: $page, first: $first, filters: $filters, orderBy: $orderBy) {
              data {
                ...StudentOutcomesRoleFields
              }
              paginatorInfo {
                ...PaginatorInfoFields
              }
            }
          }
        }
      }
    }
  }
`);

export const getSchoolStudentRoleRequestsQuery = graphql(`
  query getSchoolStudentsRoleRequests(
    $page: Int!
    $schoolId: ID!
    $filters: StudentRoleRequestFilters
    $orderBy: [SchoolStudentRoleRequestsOrderByOrderByClause!]
  ) {
    core {
      org {
        school {
          school(id: $schoolId) {
            id
            ...SchoolStudentRoleInviteCountsFields
            studentRoleRequests(page: $page, filters: $filters, orderBy: $orderBy) {
              data {
                ...StudentRoleRequestFields
              }
              paginatorInfo {
                ...PaginatorInfoFields
              }
            }
          }
        }
      }
    }
  }
`);

export const declineStudentRoleRequestMutation = graphql(`
  mutation declineStudentRoleRequest($input: StudentRoleRequestInput!) {
    core {
      org {
        school {
          declineStudentRoleRequest(input: $input) {
            request {
              ...StudentRoleRequestFields
            }
          }
        }
      }
    }
  }
`);

export const findStudentQuery = graphql(`
  query getUser($schoolId: ID!, $filters: StudentRoleFilters) {
    core {
      org {
        school {
          school(id: $schoolId) {
            id
            studentRoles(filters: $filters, first: 1, page: 1) {
              data {
                ...StudentRoleFields
                user {
                  ...AuthUserFields
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const updateStudentRoleMutation = graphql(`
  mutation updateStudentRole($input: UpdateStudentRoleInput!) {
    updateStudentRole(input: $input) {
      studentRole {
        ...StudentRoleFields
        user {
          id
          accountDetailsChecks {
            isAccountDetailsCompleted
            hasFirstName
            hasLastName
            hasVerifiedPhone
            hasState
            hasBirthDate
            hasGender
          }
        }
      }
    }
  }
`);

export const marketingPreferenceQuery = graphql(`
  query marketingPreference {
    core {
      user {
        self {
          id
          marketingPreference {
            optedIn
          }
        }
      }
    }
  }
`);

export const recordMarketingConsentMutation = graphql(`
  mutation recordMarketingConsent($input: RecordMarketingConsentInput!) {
    core {
      user {
        recordMarketingConsent(input: $input) {
          ...AuthUserFields
        }
      }
    }
  }
`);

export const RECORD_SIGN_IN_ABOUT_YOU = graphql(`
  mutation recordSignInAboutYou($input: RecordSignInAboutYouInput!) {
    core {
      user {
        recordSignInAboutYou(input: $input) {
          ...AuthUserFields
        }
      }
    }
  }
`);

export const upsertStudentRoleMutation = graphql(`
  mutation upsertStudentRole($input: UpdateStudentRoleInput!) {
    upsertStudentRole(input: $input) {
      studentRole {
        ...StudentRoleFields
      }
    }
  }
`);

export const updateTeacherRoleMutation = graphql(`
  mutation updateTeacherRole($input: UpdateTeacherRoleInput!) {
    core {
      org {
        school {
          updateTeacherRole(input: $input) {
            teacherRole {
              ...TeacherRoleFields
            }
          }
        }
      }
    }
  }
`);

export const deleteSelfMutation = graphql(`
  mutation deleteSelf {
    core {
      user {
        deleteSelf {
          success
        }
      }
    }
  }
`);

export const setPasswordMutation = graphql(`
  mutation setPassword($input: SetPasswordInput!) {
    core {
      user {
        setPassword(input: $input) {
          success
        }
      }
    }
  }
`);

export const requestPasswordChangeCodeMutation = graphql(`
  mutation requestPasswordChangeCode {
    core {
      user {
        requestPasswordChangeCode {
          success
        }
      }
    }
  }
`);

export const verifyPasswordChangeCodeMutation = graphql(`
  mutation verifyPasswordChangeCode($input: VerifyPasswordChangeCodeInput!) {
    core {
      user {
        verifyPasswordChangeCode(input: $input) {
          success
          changeToken
        }
      }
    }
  }
`);

export const fileFragment = graphql(`
  fragment FileFields on File {
    id
    userId
    type
    path
    originalName
    signedUrl
    size
    mimeType
  }
`);

export const uploadFile = graphql(`
  mutation uploadFile($input: [UploadInput!]!) {
    core {
      file {
        upload(input: $input) {
          data {
            ...FileFields
          }
        }
      }
    }
  }
`);

export const publicFileFragment = graphql(`
  fragment PublicFileFields on File {
    id
    userId
    type
    path
    originalName
    publicUrl
    size
    mimeType
    title
  }
`);

export const uploadPublicFile = graphql(`
  mutation uploadPublicFile($input: [UploadInput!]!) {
    core {
      file {
        upload(input: $input) {
          data {
            ...PublicFileFields
          }
        }
      }
    }
  }
`);

export const getSelfUploadedFiles = graphql(`
  query getSelfUploadedFiles($filters: FileFilters) {
    core {
      user {
        self {
          id
          files(filters: $filters) {
            ...FileFields
          }
        }
      }
    }
  }
`);

export const inviteStudentsMutation = graphql(`
  mutation inviteStudents($input: InviteStudentsInput!, $schoolId: ID!) {
    core {
      org {
        school {
          inviteStudents(input: $input) {
            success
            results {
              index
              email
              outcome
            }
            query {
              core {
                org {
                  school {
                    school(id: $schoolId) {
                      id
                      studentRoleInvitesCount
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const checkStudentRoleInvite = graphql(`
  query checkStudentRoleInvite($input: CheckStudentRoleInviteInput!) {
    core {
      org {
        school {
          checkStudentRoleInvite(input: $input) {
            id
            isValid
            reason
            school {
              id
              name
              label
              displayName
            }
            email
          }
        }
      }
    }
  }
`);

export const declineStudentRoleInvite = graphql(`
  mutation declineStudentRoleInvite($input: DeclineStudentRoleInviteInput!) {
    core {
      org {
        school {
          declineStudentRoleInvite(input: $input) {
            success
          }
        }
      }
    }
  }
`);

export const requestStudentInviteOtpCodeMutation = graphql(`
  mutation requestStudentInviteOtpCode($input: RequestStudentInviteOtpCodeInput!) {
    core {
      org {
        school {
          requestStudentInviteOtpCode(input: $input) {
            success
          }
        }
      }
    }
  }
`);

export const verifyStudentInviteOtpCodeMutation = graphql(`
  mutation verifyStudentInviteOtpCode($input: VerifyStudentInviteOtpCodeInput!) {
    core {
      org {
        school {
          verifyStudentInviteOtpCode(input: $input) {
            flow
          }
        }
      }
    }
  }
`);

export const setAnimationsEnabled = graphql(`
  mutation setAnimationsEnabled($animationsEnabled: Boolean!) {
    core {
      user {
        setAnimationsEnabled(animationsEnabled: $animationsEnabled) {
          animationsEnabled
        }
      }
    }
  }
`);

export const setUserAvatar = graphql(`
  mutation setUserAvatar($input: SetUserAvatarInput!) {
    core {
      user {
        setUserAvatar(input: $input) {
          id
          avatarIllustration
          avatarColor
        }
      }
    }
  }
`);

export const getSchoolRequestsAndInvites = graphql(`
  query getSchoolRequestsAndInvites {
    core {
      user {
        self {
          ...SchoolManagementUserFields
        }
      }
    }
  }
`);

export const studentRoleInvitesSelfQuery = graphql(`
  query studentRoleInvitesSelf {
    core {
      user {
        self {
          id
          studentRoleInvites {
            ...StudentRoleInviteFields
          }
        }
      }
    }
  }
`);

export const respondStudentRoleInvite = graphql(`
  mutation respondStudentRoleInvite($input: RespondStudentRoleInviteInput!) {
    core {
      org {
        school {
          respondStudentRoleInvite(input: $input) {
            success
            user {
              ...SchoolManagementUserFields
            }
          }
        }
      }
    }
  }
`);

export const deleteRole = graphql(`
  mutation deleteRole($input: DeleteRoleInput!) {
    core {
      org {
        school {
          deleteRole(input: $input) {
            success
            user {
              ...SchoolManagementUserFields
            }
          }
        }
      }
    }
  }
`);

export const requestSchoolVerificationMutation = graphql(`
  mutation requestSchoolVerification($schoolId: ID!) {
    core {
      org {
        school {
          requestSchoolVerification(schoolId: $schoolId) {
            school {
              id
              verified
            }
          }
        }
      }
    }
  }
`);

export const bulkDeleteStudentRolesMutation = graphql(`
  mutation bulkDeleteStudentRoles($input: BulkActionStudentRolesInput!) {
    core {
      org {
        school {
          bulkDeleteStudentRoles(input: $input) {
            success
            skippedManaged
          }
        }
      }
    }
  }
`);

export const deleteSchoolRequest = graphql(`
  mutation deleteSchoolRequest($input: StudentRoleRequestInput!) {
    core {
      org {
        school {
          deleteStudentRoleRequest(input: $input) {
            success
            user {
              ...SchoolManagementUserFields
            }
          }
        }
      }
    }
  }
`);

export const bulkDeclineStudentRoleRequestsMutation = graphql(`
  mutation bulkDeclineStudentRoleRequests($input: BulkDeclineStudentRoleRequestsInput!) {
    core {
      org {
        school {
          bulkDeclineStudentRoleRequests(input: $input) {
            success
          }
        }
      }
    }
  }
`);

export const getSchoolStudentInvitesQuery = graphql(`
  query getSchoolStudentInvites(
    $page: Int!
    $schoolId: ID!
    $filters: StudentRoleInviteFilters
    $orderBy: [SchoolStudentRoleInvitesOrderByOrderByClause!]
  ) {
    core {
      org {
        school {
          school(id: $schoolId) {
            id
            ...SchoolStudentRoleInviteCountsFields
            studentRoleInvites(page: $page, filters: $filters, orderBy: $orderBy) {
              data {
                ...SchoolStudentRoleInviteFields
              }
              paginatorInfo {
                ...PaginatorInfoFields
              }
            }
          }
        }
      }
    }
  }
`);

export const deleteStudentRoleInviteMutation = graphql(`
  mutation deleteStudentRoleInvite($input: StudentRoleInviteInput!) {
    core {
      org {
        school {
          deleteStudentRoleInvite(input: $input) {
            success
          }
        }
      }
    }
  }
`);

export const resendStudentRoleInviteMutation = graphql(`
  mutation resendStudentRoleInvite($input: StudentRoleInviteInput!) {
    core {
      org {
        school {
          resendStudentRoleInvite(input: $input) {
            success
            outcome
            studentRoleInvite {
              ...SchoolStudentRoleInviteFields
            }
          }
        }
      }
    }
  }
`);

export const bulkDeleteStudentRoleInvitesMutation = graphql(`
  mutation bulkDeleteStudentRoleInvites($input: BulkActionStudentRoleInvitesInput!) {
    core {
      org {
        school {
          bulkDeleteStudentRoleInvites(input: $input) {
            success
          }
        }
      }
    }
  }
`);

export const bulkResendStudentRoleInvitesMutation = graphql(`
  mutation bulkResendStudentRoleInvites($input: BulkActionStudentRoleInvitesInput!) {
    core {
      org {
        school {
          bulkResendStudentRoleInvites(input: $input) {
            success
            matched
            resent
            skippedPending
            skippedExisting
          }
        }
      }
    }
  }
`);

export const studentRoleInviteUpdatedSubscription = graphql(`
  subscription studentRoleInviteUpdated($schoolId: ID!) {
    studentRoleInviteUpdated(schoolId: $schoolId) {
      ...SchoolStudentRoleInviteFields
    }
  }
`);

export const studentRoleRequestCreatedSubscription = graphql(`
  subscription studentRoleRequestCreated($schoolId: ID!) {
    studentRoleRequestCreated(schoolId: $schoolId) {
      id
      school {
        id
        studentRoleRequestsCount
      }
      deletedAt
    }
  }
`);

export const getUserEmailLogins = graphql(`
  query getUserEmailLogins {
    core {
      user {
        self {
          id
          emails {
            ...UserLoginFields
          }
        }
      }
    }
  }
`);

export const addSecondaryEmailLoginMutation = graphql(`
  mutation addSecondaryEmailLogin($input: AddSecondaryEmailUserLoginInput!) {
    core {
      user {
        addSecondaryEmailLogin(input: $input) {
          success
          query {
            core {
              user {
                self {
                  id
                  emails {
                    ...UserLoginFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const deleteSecondaryEmailLoginMutation = graphql(`
  mutation deleteSecondaryEmailLogin($input: DeleteSecondaryEmailUserLoginInput!) {
    core {
      user {
        deleteSecondaryEmailLogin(input: $input) {
          success
          query {
            core {
              user {
                self {
                  id
                  emails {
                    ...UserLoginFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const verifyEmailVerificationCodeMutation = graphql(`
  mutation verifyEmailVerificationCode($input: VerifyEmailVerificationCodeInput!) {
    core {
      user {
        verifyEmailVerificationCode(input: $input) {
          success
          query {
            core {
              user {
                self {
                  id
                  emails {
                    ...UserLoginFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const resendEmailVerificationMutation = graphql(`
  mutation resendEmailVerificationCode($input: ResendEmailVerificationCodeInput!) {
    core {
      user {
        resendEmailVerificationCode(input: $input) {
          success
          query {
            core {
              user {
                self {
                  id
                  emails {
                    ...UserLoginFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const addTeacherRoleToStudentMutation = graphql(`
  mutation addTeacherRoleToStudent {
    core {
      user {
        addTeacherRoleToStudent {
          success
          query {
            core {
              user {
                self {
                  ...AuthUserFields
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const addStudentRoleToTeacherMutation = graphql(`
  mutation addStudentRoleToTeacher {
    core {
      user {
        addStudentRoleToTeacher {
          success
          query {
            core {
              user {
                self {
                  ...AuthUserFields
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const downloadStudentEnquiryMutation = graphql(`
  mutation downloadStudentEnquiry($input: DownloadStudentEnquiryInput!) {
    core {
      user {
        downloadStudentEnquiry(input: $input) {
          csvData
          error
        }
      }
    }
  }
`);

export const requestSMSVerificationCode = graphql(`
  mutation requestSMSVerificationCode($input: RequestSMSVerificationCodeInput!) {
    requestSMSVerificationCode(input: $input) {
      success
    }
  }
`);
export const verifyAndLinkPhoneNumber = graphql(`
  mutation verifyAndLinkPhoneNumber($input: VerifyAndLinkPhoneNumberInput!) {
    verifyAndLinkPhoneNumber(input: $input) {
      success
      query {
        core {
          user {
            self {
              ...AuthUserFields
            }
          }
        }
      }
    }
  }
`);

export const checkSignupAgeQuery = graphql(`
  query checkSignupAge($birthMonthYear: String!) {
    core {
      site {
        checkSignupAge(birthMonthYear: $birthMonthYear) {
          canCreateAccount
          canBeMarketed
        }
      }
    }
  }
`);
